/**
 * Per-block "show diagram" state for mermaid code blocks.
 *
 * A mermaid block is a normal editable code block by DEFAULT; the toolbar
 * toggle opts into rendering the diagram instead. This tiny module-level store
 * holds the set of block positions currently showing the diagram. The toolbar
 * toggles a position; the MermaidExtension subscribes and only mounts a diagram
 * widget for positions in the set (and rebuilds its decorations on change).
 *
 * Kept as a plain store (not a ProseMirror plugin) on purpose — the state is
 * pure view preference, not document data, so it must never enter the CRDT or
 * the transaction history. Keyed by document position; positions are short-
 * lived (the extension re-reads on every doc change), so stale entries after
 * heavy edits are harmless (a block simply reverts to code view).
 */

const shown = new Set<number>();
const listeners = new Set<() => void>();

/** True if the mermaid block at `pos` is currently showing its diagram. */
export function isMermaidDiagramShown(pos: number): boolean {
  return shown.has(pos);
}

/** Toggle the diagram on/off for the block at `pos` and notify subscribers. */
export function toggleMermaidDiagram(pos: number): void {
  if (shown.has(pos)) {
    shown.delete(pos);
  } else {
    shown.add(pos);
  }
  listeners.forEach((fn) => fn());
}

/** Subscribe to view changes (the extension rebuilds its decorations). */
export function subscribeMermaidView(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test-only: clear all state between tests. */
export function _resetMermaidViewStore(): void {
  shown.clear();
  listeners.clear();
}
